import { FC, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { PerModal } from '../Modal'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import {
  CompanyTiny,
  CompanyScope,
  Country,
  KudosCompanyCountryVisibilityFilterInput,
  KudosCompanyCountryVisibilityInput,
} from '../../../types'
import { createOrEditKudosCompanyCountryVisibility } from '../../../graphql/kudos/useKudosMutations'
import { showNotification } from '../Notification/notification'
import { CompanySearchSelect } from '../../../modules/kudos/components/CompanySearchSelect'
import { useCompanySearch } from '../../../graphql/company/hooks/useCompanySearch'
import { CountrySelect } from '../../../modules/kudos/components/CountrySelect'
import { Switch } from 'antd'
import { useAuth } from '../../../context/auth-context'
import { getCountriesWhereUserIsHrAdmin } from '../../../modules/user/util'

export interface CreateKudosCompanyCountryVisibilityModalProps {
  visible: boolean
  closeModal: () => void
  onSuccess: () => void
  filters: KudosCompanyCountryVisibilityFilterInput
  resetFiltersAfterVisibilityChange: () => void
}

export const CreateKudosCompanyCountryVisibilityModal: FC<CreateKudosCompanyCountryVisibilityModalProps> = ({
  visible,
  closeModal,
  onSuccess,
  filters,
  resetFiltersAfterVisibilityChange,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()

  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const [selectedCompany, setSelectedCompany] = useState<CompanyTiny | null>(null)
  const [kudosEnabled, setKudosEnabled] = useState<boolean>(true)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const companySearch = useCompanySearch(CompanyScope.HrUser)
  const hrAdminCountries = user.roles ? getCountriesWhereUserIsHrAdmin(user.roles) : []

  const handleCreate = async () => {
    if (!selectedCountry || !selectedCompany) return

    setIsLoading(true)
    setErrorMessage('')

    const input: KudosCompanyCountryVisibilityInput = {
      companyId: selectedCompany.id,
      country: selectedCountry,
      kudosEnabled: kudosEnabled,
    }
    const result = await createOrEditKudosCompanyCountryVisibility(input, filters)

    if (!!result.data && !result.errors) {
      resetFormAndCloseModal()
      resetFiltersAfterVisibilityChange()
      onSuccess()
      showNotification({
        type: 'success',
        message: t('kudos.notifications.createCompanyCountryVisibility.successTitle'),
        description: t('kudos.notifications.createCompanyCountryVisibility.successDescription'),
      })
    } else {
      const errorMessage =
        result.errors?.[0]?.extensions?.validationErrors?.[0]?.message || t('kudos.settings.createCompanyCountryVisibilityModal.error')
      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetFormAndCloseModal = () => {
    setSelectedCountry(null)
    setSelectedCompany(null)
    setKudosEnabled(true)
    setErrorMessage('')
    setIsLoading(false)
    companySearch.handleClear()
    closeModal()
  }

  const handleCountrySelect = (country: Country | null) => {
    setSelectedCountry(country)
  }

  const handleCompanyOptionClick = (company: CompanyTiny) => {
    setSelectedCompany(company)
    companySearch.handleClear()
  }

  const handleCompanyClear = () => {
    setSelectedCompany(null)
    companySearch.handleClear()
  }

  const isFormValid = selectedCompany && selectedCountry

  return (
    <PerModal
      title={{ heading: t('kudos.settings.createCompanyCountryVisibilityModal.title') }}
      size="large"
      centered
      visible={visible}
      onCancel={resetFormAndCloseModal}
      footer={{
        cancelBtn: {
          text: t('global.button.cancel'),
          onClick: resetFormAndCloseModal,
          icon: 'close_stroke',
          visible: !isLoading,
        },
        confirmBtn: {
          onClick: handleCreate,
          disabled: !isFormValid || isLoading,
          isProcessing: { active: isLoading },
        },
      }}
    >
      <PerFlexRow direction="column">
        {/* Disclaimer 1 */}
        <PerText fontSize="m">{t('kudos.settings.createCompanyCountryVisibilityModal.disclaimer1')}</PerText>

        {/* Disclaimer 2 */}
        <PerText fontSize="m">{t('kudos.settings.createCompanyCountryVisibilityModal.disclaimer2')}</PerText>

        {/* Disclaimer 3 */}
        <PerText fontSize="m" mb="m" mt="xs">
          <Trans i18nKey="kudos.settings.createCompanyCountryVisibilityModal.disclaimer3" components={[<b></b>]} />
        </PerText>

        {/* Company Selection */}
        <PerFlexRow direction="column" width="50%" mb="m">
          <PerText fontSize="m" fontWeight="bold" mb="xxs">
            {t('kudos.settings.createCompanyCountryVisibilityModal.company')}
          </PerText>
          <CompanySearchSelect
            size="default"
            searchTerm={companySearch.searchTerm}
            onSearch={companySearch.handleSearch}
            onClear={handleCompanyClear}
            companies={companySearch.companies}
            loading={companySearch.loading}
            shouldShowOptions={companySearch.shouldShowOptions}
            onOptionClick={handleCompanyOptionClick}
            placeholder={t('kudos.settings.createCompanyCountryVisibilityModal.companyPlaceholder')}
            selectedCompany={selectedCompany}
          />
        </PerFlexRow>

        {/* Country Selection */}
        <PerFlexRow direction="column" width="50%" mb="m">
          <PerText fontSize="m" fontWeight="bold" mb="xxs">
            {t('kudos.settings.createCompanyCountryVisibilityModal.country')}
          </PerText>
          <CountrySelect
            size="default"
            countries={hrAdminCountries}
            selectedCountry={selectedCountry}
            onCountrySelect={handleCountrySelect}
            placeholder={t('kudos.settings.createCompanyCountryVisibilityModal.countryPlaceholder')}
            loading={false}
          />
        </PerFlexRow>

        {/* Visibility Selection */}
        <PerFlexRow direction="column">
          <PerText fontSize="m" fontWeight="bold" mb="xxs">
            {t('kudos.settings.createCompanyCountryVisibilityModal.visibility')}
          </PerText>
          <PerFlexRow>
            <Switch
              checked={kudosEnabled}
              onChange={() => {
                setKudosEnabled(!kudosEnabled)
              }}
            />
          </PerFlexRow>
        </PerFlexRow>

        {errorMessage && (
          <PerText color="error" fontSize="m" mt="m">
            {errorMessage}
          </PerText>
        )}
      </PerFlexRow>
    </PerModal>
  )
}

export default CreateKudosCompanyCountryVisibilityModal
